import { BoutonVersionsRecu } from './recus/RecuVersions';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import RefreshButton from './ui/RefreshButton';

import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { FileText, Calendar, Building2, Download, CheckCircle, Archive, ArchiveRestore, Edit3, Search, TrendingUp, Clock, BarChart3, Layers, ChevronDown, Eye, FilePlus, Trash2, Mail, Send, Bell, MailX, ListChecks } from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import SouscriptionModificationDialog from './SouscriptionModificationDialog';
import SouscriptionDocumentsPreviewDialog from './SouscriptionDocumentsPreviewDialog';
import { RecuEnvoisHistorique, JournalEnvoisDialog, JournalEnvoisButton } from './RecuEnvois';
import { EnvoiEmailDialog } from './EnvoiEmailDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getMontantTTC = (s) => {
  if (s.montant_global && s.montant_global > 0) return s.montant_global;
  const montantHT = parseFloat(s.montant_ht_contribution || s.montant_ht || s.tarification?.montant_ht || 0);
  return montantHT > 0 ? montantHT * 1.275 : 0;
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';

const STATUS_CFG = {
  validee_payee: { label: 'Validee', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  en_attente: { label: 'En attente', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  signee: { label: 'Signee', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  archived: { label: 'Archivee', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  default: { label: 'Autre', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const isSansRecu = (s) => s.statut === 'validee' && s.paiement_valide && !s.archive && !s.recu_envoye_le;

function getStatusKey(s) {
  if (s.archive) return 'archived';
  if (s.statut === 'validee' && s.paiement_valide) return 'validee_payee';
  if (s.statut === 'en_attente_paiement') return 'en_attente';
  if (s.statut === 'signee') return 'signee';
  return 'default';
}

function StatusBadge({ souscription }) {
  const key = getStatusKey(souscription);
  const cfg = STATUS_CFG[key];
  return <Badge className={cn("text-[10px] font-semibold border", cfg.cls)}>{cfg.label}</Badge>;
}

export default function SouscriptionsListe() {
  const { getAuthHeader, user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [souscriptions, setSouscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModificationDialog, setShowModificationDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [selectedSouscription, setSelectedSouscription] = useState(null);
  const [dialogMode, setDialogMode] = useState('edit');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [envoisRefresh, setEnvoisRefresh] = useState(0);
  const [bulkSending, setBulkSending] = useState(false);
  const [journalOpen, setJournalOpen] = useState(false);
  const [emailDialog, setEmailDialog] = useState(null);

  useEffect(() => { fetchSouscriptions(); }, []);

  const fetchSouscriptions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/souscriptions-rcd`, { headers: getAuthHeader() });
      const sorted = (response.data || [])
        .map((s) => ({ ...s, archive: s.archived === false ? false : !!(s.archive || s.archived) }))
        .sort((a, b) => new Date(b.date_creation || b.created_at || 0) - new Date(a.date_creation || a.created_at || 0));
      setSouscriptions(sorted);
      setSelectedIds([]);
    } catch {
      toast.error('Erreur lors du chargement des souscriptions');
    } finally {
      setLoading(false);
    }
  };

  const isNotExpired = (s) => {
    const dateFin = s.date_fin || s.periode_adhesion_fin || s.date_expiration;
    if (!dateFin) return true;
    try {
      let d;
      if (dateFin.includes('T')) d = new Date(dateFin);
      else if (dateFin.includes('/')) { const [day, month, year] = dateFin.split('/'); d = new Date(year, month - 1, day); }
      else d = new Date(dateFin);
      return d >= new Date();
    } catch { return true; }
  };

  const stats = useMemo(() => {
    const validees = souscriptions.filter(s => s.statut === 'validee' && s.paiement_valide && !s.archive && isNotExpired(s));
    const enAttente = souscriptions.filter(s => (s.statut === 'en_attente_paiement' || s.statut === 'signee') && !s.archive);
    const archived = souscriptions.filter(s => s.archive);
    return {
      total: souscriptions.filter(s => !s.archive).length,
      validees: validees.length,
      enAttente: enAttente.length,
      archived: archived.length,
      totalMontant: validees.reduce((sum, s) => sum + getMontantTTC(s), 0)
    };
  }, [souscriptions]);

  const filteredSouscriptions = useMemo(() => {
    return souscriptions.filter(s => {
      let statusMatch = true;
      if (filter === 'all') statusMatch = !s.archive;
      else if (filter === 'validee') statusMatch = s.statut === 'validee' && s.paiement_valide && !s.archive && isNotExpired(s);
      else if (filter === 'en_attente') statusMatch = (s.statut === 'en_attente_paiement' || s.statut === 'signee') && !s.archive;
      else if (filter === 'archived') statusMatch = s.archive === true;
      else if (filter === 'sans_recu') statusMatch = isSansRecu(s);
      const searchMatch = !searchTerm || s.raison_sociale?.toLowerCase().includes(searchTerm.toLowerCase()) || s.numero_police?.toLowerCase().includes(searchTerm.toLowerCase()) || s.siret?.includes(searchTerm);
      return statusMatch && searchMatch;
    });
  }, [souscriptions, filter, searchTerm]);

  const toggleExpand = useCallback((id) => {
    setExpandedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }, []);

  const handleSelectAll = (e) => { setSelectedIds(e.target.checked ? filteredSouscriptions.map(s => s.id) : []); };
  const handleSelectOne = (e, id) => { e.stopPropagation(); setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]); };

  const handleDownloadAttestation = async (e, souscriptionId) => {
    if (e) e.stopPropagation();
    try {
      toast.info('Telechargement attestation signee...');
      const response = await axios.get(`${API}/souscriptions/${souscriptionId}/download-pack1?draft=false`, { headers: getAuthHeader(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.download = `ATTESTATION_SIGNEE_${souscriptionId}.pdf`; document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success('Attestation signee telechargee !');
    } catch { toast.error('Erreur telechargement attestation'); }
  };

  const handleDownloadCPCG = async (e, souscriptionId) => {
    if (e) e.stopPropagation();
    try {
      toast.info('Telechargement CP/CG signes...');
      const response = await axios.get(`${API}/souscriptions/${souscriptionId}/download-pack2?draft=false`, { headers: getAuthHeader(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.download = `CP_CG_SIGNES_${souscriptionId}.pdf`; document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success('CP/CG signes telecharges !');
    } catch { toast.error('Erreur telechargement CP/CG'); }
  };

  const handleApercuRecu = async (e, souscriptionId) => {
    if (e) e.stopPropagation();
    try {
      toast.info('Ouverture du recu...');
      const response = await axios.get(`${API}/souscriptions/${souscriptionId}/download-recu`, { headers: getAuthHeader(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Recu non disponible'); }
  };

  const handleEnvoyerRecu = async (e, s, askEmail = false) => {
    if (e) e.stopPropagation();
    const defaultEmail = s.email || s.membre?.email || '';
    const email = askEmail ? window.prompt('Adresse email du destinataire :', defaultEmail) : defaultEmail;
    if (!email) { toast.error('Aucune adresse email pour ce membre'); return; }
    try {
      toast.info('Envoi du recu...');
      const res = await axios.post(`${API}/souscriptions/admin/${s.id}/share`, { recipients: [email], method: 'email', include_recu: true, message: 'Veuillez trouver ci-joint votre recu de contribution solidaire ESS.' }, { headers: getAuthHeader() });
      if (res.data?.emails_sent > 0) toast.success('Recu envoye a ' + email);
      else toast.error(res.data?.message || 'Echec envoi');
      fetchSouscriptions();
      setEnvoisRefresh((k) => k + 1);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de l'envoi du recu"); }
  };

  const handleRelancerRecu = (e, s) => {
    if (e) e.stopPropagation();
    setEmailDialog({ mode: 'relance', s });
  };

  const handleEnvoiGroupe = () => setEmailDialog({ mode: 'groupe' });

  const sendRelance = async (s, contenu) => {
    try {
      const res = await axios.post(`${API}/souscriptions/admin/${s.id}/relancer-recu`, contenu, { headers: getAuthHeader() });
      toast.success('Relance envoyee a ' + res.data.email);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur lors de la relance'); throw err; }
  };

  const sendGroupe = async (contenu) => {
    setBulkSending(true);
    try {
      const { data } = await axios.post(`${API}/souscriptions/admin/envois/groupe`, { souscription_ids: selectedIds, ...contenu }, { headers: getAuthHeader() });
      toast.success(`${data.envoyes.length} envoye(s), ${data.ignores.length} ignore(s) (deja envoye), ${data.echecs.length} echec(s)`, { duration: 8000 });
      if (data.echecs.length) toast.error('Echecs : ' + data.echecs.map((x) => `${x.raison_sociale || x.id} (${x.raison})`).join(', '), { duration: 10000 });
      setSelectedIds([]);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de l'envoi groupe"); throw err; }
    finally { setBulkSending(false); }
  };

  const handleEmailDialogSend = async (contenu) => {
    if (emailDialog.mode === 'relance') await sendRelance(emailDialog.s, contenu);
    else await sendGroupe(contenu);
    fetchSouscriptions();
    setEnvoisRefresh((k) => k + 1);
  };

  const handleOpenDialog = (e, s, mode = 'view') => {
    if (e) e.stopPropagation();
    if (mode === 'view') {
      setSelectedSouscription(s);
      setTimeout(() => setShowPreviewDialog(true), 0);
      return;
    }
    setSelectedSouscription(s); setDialogMode(mode);
    setTimeout(() => setShowModificationDialog(true), 0);
  };

  const handleDeleteSouscription = async (e, s) => {
    if (e) e.stopPropagation();
    // V37.44 — Le DELETE est desormais un soft-delete (archivage reversible).
    // On previent l'utilisateur que l'action est reversible depuis l'onglet
    // Archivees, ce qui rassure et evite l'effet "perte definitive".
    if (!window.confirm(
      `Archiver la souscription ${s.raison_sociale} (${s.numero_police}) ?\n\n` +
      `Cette suppression est REVERSIBLE : la souscription apparaitra dans l'onglet ` +
      `« Archivees » d'ou vous pourrez la restaurer.`,
    )) return;
    try {
      const response = await axios.delete(`${API}/souscriptions-rcd/${s.id}`, { headers: getAuthHeader() });
      const data = response?.data || {};
      if (data.soft_delete) {
        toast.success(`Souscription ${s.raison_sociale} archivee — restaurable depuis l'onglet Archivees`);
      } else {
        toast.success(`Souscription ${s.raison_sociale} supprimee`);
      }
      // Recharger pour que l'item passe en archived au lieu de disparaitre
      fetchSouscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // V37.44 — Restauration d'une souscription archivee (depuis l'onglet Archivees)
  const handleRestoreSouscription = async (e, s) => {
    if (e) e.stopPropagation();
    if (!window.confirm(
      `Restaurer la souscription ${s.raison_sociale} (${s.numero_police}) ?\n\n` +
      `Elle redeviendra active avec son statut precedent.`,
    )) return;
    try {
      const response = await axios.post(
        `${API}/souscriptions-rcd/${s.id}/restore`,
        {},
        { headers: getAuthHeader() },
      );
      const data = response?.data || {};
      if (data.restored) {
        toast.success(`Souscription ${s.raison_sociale} restauree`);
      } else {
        toast.info(data.message || 'Souscription deja active');
      }
      fetchSouscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la restauration');
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) { toast.error('Aucune souscription selectionnee'); return; }
    const t = action === 'validate' ? 'Valider' : action === 'archive' ? 'Archiver' : 'Desarchiver';
    if (!window.confirm(`${t} ${selectedIds.length} souscription(s) ?`)) return;
    try {
      const endpoint = action === 'validate' ? 'bulk-validate' : action === 'archive' ? 'bulk-archive' : 'bulk-unarchive';
      const response = await axios.post(`${API}/souscriptions-rcd/${endpoint}`, { souscription_ids: selectedIds }, { headers: getAuthHeader() });
      toast.success(response.data.message); fetchSouscriptions();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const FILTERS = [
    { key: 'all', label: 'Tous', icon: Layers },
    { key: 'validee', label: 'Validees', icon: CheckCircle },
    { key: 'en_attente', label: 'En attente', icon: Clock },
    { key: 'sans_recu', label: `Sans reçu (${souscriptions.filter(isSansRecu).length})`, icon: MailX },
    { key: 'archived', label: 'Archivees', icon: Archive },
  ];

  return (
    <div className="space-y-4" data-testid="souscriptions-liste">
      {/* Header compact */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Toutes les Souscriptions</h2>
          <p className="text-sm text-gray-500">Vue d'ensemble complete de votre portefeuille</p>
        </div>
        <div className="flex items-center gap-2">
          <JournalEnvoisButton onClick={() => setJournalOpen(true)} />
          <RefreshButton
            onRefresh={fetchSouscriptions}
            loading={loading}
            successMessage="Souscriptions actualisées"
            variant="ghost"
            testId="souscriptions-refresh-btn"
          />
        </div>
      </div>
      <JournalEnvoisDialog open={journalOpen} onOpenChange={setJournalOpen} />
      {emailDialog && (
        <EnvoiEmailDialog
          open={!!emailDialog}
          onOpenChange={(o) => { if (!o) setEmailDialog(null); }}
          title={emailDialog.mode === 'relance' ? `Relancer ${emailDialog.s.raison_sociale || ''}` : `Envoi groupé des reçus (${selectedIds.length})`}
          description={emailDialog.mode === 'relance' ? `Destinataire : ${emailDialog.s.email || 'email du membre'}` : 'Les membres ayant déjà reçu leur reçu seront ignorés.'}
          defaultTemplateId={emailDialog.mode === 'relance' ? 'builtin_relance_courtoise' : 'builtin_envoi'}
          onSend={handleEmailDialogSend}
        />
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[
          { label: 'Total actif', value: stats.total, icon: BarChart3, color: 'text-gray-900', filterKey: 'all' },
          { label: 'Validees', value: stats.validees, icon: CheckCircle, color: 'text-emerald-600', filterKey: 'validee' },
          { label: 'En attente', value: stats.enAttente, icon: Clock, color: 'text-amber-600', filterKey: 'en_attente' },
          { label: 'Archivees', value: stats.archived, icon: Archive, color: 'text-slate-500', filterKey: 'archived' },
          { label: 'Volume Global', value: `${(stats.totalMontant / 1000).toFixed(1)}K`, icon: TrendingUp, color: 'text-blue-600', isMoney: true },
        ].map((s, i) => (
          <button key={i} onClick={() => s.filterKey && setFilter(s.filterKey)}
            className={cn("flex items-center gap-3 p-3 rounded-lg border bg-white transition-all text-left",
              s.filterKey === filter ? "border-blue-300 shadow-sm bg-blue-50/50" : "border-gray-200 hover:border-gray-300 hover:shadow-sm",
              !s.filterKey && "cursor-default"
            )}>
            <s.icon className={cn("h-4 w-4 shrink-0", s.color)} />
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">{s.label}</p>
              <p className={cn("text-lg font-bold", s.color)}>{s.isMoney ? `${s.value} \u20AC` : s.value}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Search + filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input placeholder="Rechercher par raison sociale, N police, SIRET..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10" data-testid="souscriptions-search" />
        </div>
        <div className="flex gap-1 flex-wrap">
          {FILTERS.map(f => (
            <Button key={f.key} variant={filter === f.key ? 'default' : 'outline'} size="sm"
              onClick={() => setFilter(f.key)}
              className={cn("gap-1.5 text-xs", filter === f.key && "bg-slate-700 hover:bg-slate-800")}
              data-testid={`filter-${f.key}`}>
              <f.icon className="h-3.5 w-3.5" /> {f.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5">
          <span className="text-sm font-medium text-blue-800">
            <Badge className="bg-blue-600 text-white mr-2 font-semibold">{selectedIds.length}</Badge>souscription(s) selectionnee(s)
          </span>
          <div className="flex gap-1.5">
            {filter !== 'archived' && (
              <>
                <Button size="sm" onClick={() => handleBulkAction('validate')} className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs"><CheckCircle className="h-3.5 w-3.5 mr-1" />Valider</Button>
                <Button size="sm" onClick={handleEnvoiGroupe} disabled={bulkSending} className="bg-indigo-600 hover:bg-indigo-700 h-8 text-xs" data-testid="bulk-envoyer-recus-btn"><Send className="h-3.5 w-3.5 mr-1" />{bulkSending ? 'Envoi...' : 'Envoyer les recus'}</Button>
                <Button size="sm" variant="outline" onClick={() => handleBulkAction('archive')} className="h-8 text-xs"><Archive className="h-3.5 w-3.5 mr-1" />Archiver</Button>
              </>
            )}
            {filter === 'archived' && (
              <Button size="sm" onClick={() => handleBulkAction('unarchive')} className="bg-blue-600 hover:bg-blue-700 h-8 text-xs"><ArchiveRestore className="h-3.5 w-3.5 mr-1" />Desarchiver</Button>
            )}
          </div>
        </div>
      )}

      {/* Select all + count */}
      <div className="flex items-center gap-3 px-1">
        <input type="checkbox" checked={selectedIds.length === filteredSouscriptions.length && filteredSouscriptions.length > 0}
          onChange={handleSelectAll} className="w-4 h-4 rounded border-gray-300 text-blue-600" />
        <span className="text-sm text-gray-500">{filteredSouscriptions.length} resultat(s)</span>
        {filter === 'sans_recu' && filteredSouscriptions.length > 0 && (
          <Button size="sm" variant="outline" onClick={() => setSelectedIds(filteredSouscriptions.map(s => s.id))} className="h-7 text-xs gap-1.5 border-indigo-200 text-indigo-700" data-testid="select-all-sans-recu-btn">
            <ListChecks className="h-3.5 w-3.5" /> Tout sélectionner ({filteredSouscriptions.length}) pour envoi groupé
          </Button>
        )}
      </div>

      {/* Liste depliable */}
      {filteredSouscriptions.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-700 font-medium">Aucune souscription trouvee</p>
          <p className="text-sm text-gray-400">Modifiez vos filtres ou effectuez une nouvelle recherche</p>
        </div>
      ) : (
        <div className="space-y-1" data-testid="souscriptions-list">
          {filteredSouscriptions.map((s) => {
            const isExpanded = expandedIds.has(s.id);
            const montant = getMontantTTC(s);
            return (
              <div key={s.id} className={cn("rounded-lg border transition-all duration-150", isExpanded ? "border-blue-200 shadow-sm bg-white" : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm")} data-testid={`souscription-row-${s.id}`}>
                {/* Ligne compacte */}
                <div className="flex items-center gap-3 px-4 py-2.5">
                  <input type="checkbox" checked={selectedIds.includes(s.id)} onChange={(e) => handleSelectOne(e, s.id)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 shrink-0" onClick={(e) => e.stopPropagation()} />
                  <button onClick={() => toggleExpand(s.id)} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-testid={`souscription-toggle-${s.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className={cn("font-medium text-sm truncate", isExpanded ? "text-blue-700" : "text-gray-900")}>{s.raison_sociale}</p>
                      <p className="text-[10px] text-gray-400 font-mono truncate">{s.numero_police?.slice(0, 25)}</p>
                    </div>
                    <div className="hidden sm:flex items-center gap-1 text-xs text-gray-500 shrink-0">
                      <Building2 className="h-3 w-3" /><span className="font-mono">{s.siret?.slice(0, 9)}...</span>
                    </div>
                    <div className="hidden md:flex items-center gap-1 text-xs text-gray-500 shrink-0">
                      <Calendar className="h-3 w-3" /><span>{formatDate(s.date_creation)}</span>
                    </div>
                    <StatusBadge souscription={s} />
                    <span className="text-sm font-bold text-emerald-700 tabular-nums shrink-0">{montant.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}&euro;</span>
                    <ChevronDown className={cn("h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200", isExpanded && "rotate-180 text-blue-500")} />
                  </button>
                </div>

                {/* Panneau depliant */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-1 border-t border-gray-100 animate-in fade-in slide-in-from-top-1 duration-150" data-testid={`souscription-details-${s.id}`}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Col 1: Entreprise */}
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Entreprise</h4>
                        <div className="space-y-1.5 text-sm">
                          <p className="font-medium text-gray-900">{s.raison_sociale}</p>
                          <p className="text-gray-600 flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-gray-400" />SIRET: <span className="font-mono">{s.siret || '-'}</span></p>
                          {s.email && <p className="text-gray-600 truncate">{s.email}</p>}
                          {s.telephone && <p className="text-gray-600">{s.telephone}</p>}
                        </div>
                      </div>

                      {/* Col 2: Details souscription */}
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Details</h4>
                        <div className="space-y-1.5 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">N police:</span>
                            <span className="font-mono text-xs font-medium text-gray-800 truncate max-w-[180px]" title={s.numero_police}>{s.numero_police}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Statut:</span>
                            <StatusBadge souscription={s} />
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Date creation:</span>
                            <span className="text-gray-700">{formatDate(s.date_creation)}</span>
                          </div>
                          {(s.date_fin || s.periode_adhesion_fin) && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Date fin:</span>
                              <span className="text-gray-700">{formatDate(s.date_fin || s.periode_adhesion_fin)}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-gray-500">Montant Global:</span>
                            <span className="font-bold text-emerald-700">{montant.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} &euro;</span>
                          </div>
                          {s.mode_paiement && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Mode paiement:</span>
                              <span className="text-gray-700 capitalize">{s.mode_paiement}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Col 3: Actions */}
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Actions</h4>
                        <div className="flex flex-wrap gap-1.5">
                          <Button variant="outline" size="sm" onClick={(e) => handleOpenDialog(e, s, 'view')} className="h-8 text-xs gap-1.5" data-testid={`view-souscription-${s.id}`}>
                            <Eye className="h-3.5 w-3.5" /> Voir
                          </Button>
                          <Button variant="outline" size="sm" onClick={(e) => handleOpenDialog(e, s, 'edit')} className="h-8 text-xs gap-1.5" data-testid={`edit-souscription-${s.id}`}>
                            <Edit3 className="h-3.5 w-3.5" /> Modifier
                          </Button>
                          {(s.signature_electronique || s.paiement_valide || s.statut === 'signee' || s.statut === 'validee') && (
                            <>
                              <Button variant="outline" size="sm" onClick={(e) => handleDownloadAttestation(e, s.id)} className="h-8 text-xs gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid={`download-pack1-${s.id}`}>
                                <Download className="h-3.5 w-3.5" /> Attestation signee
                              </Button>
                              <Button variant="outline" size="sm" onClick={(e) => handleDownloadCPCG(e, s.id)} className="h-8 text-xs gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50" data-testid={`download-pack2-${s.id}`}>
                                <FileText className="h-3.5 w-3.5" /> CP/CG signes
                              </Button>
                              <Button variant="outline" size="sm" onClick={(e) => handleEnvoyerRecu(e, s, true)} className="h-8 text-xs gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50" data-testid={`envoyer-membre-${s.id}`}>
                                <Mail className="h-3.5 w-3.5" /> Envoyer au membre
                              </Button>
                              <Button variant="outline" size="sm" onClick={(e) => handleApercuRecu(e, s.id)} className="h-8 text-xs gap-1.5 border-amber-200 text-amber-700 hover:bg-amber-50" data-testid={`apercu-recu-${s.id}`}>
                                <Eye className="h-3.5 w-3.5" /> Aperçu reçu
                              </Button>
                              <BoutonVersionsRecu produit="rcds" ident={s.id} titre={s.numero_police || s.raison_sociale || s.id} className="h-8" />
                              <Button variant="outline" size="sm" onClick={(e) => handleEnvoyerRecu(e, s)} className="h-8 text-xs gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`envoyer-recu-${s.id}`}>
                                <Send className="h-3.5 w-3.5" /> Envoyer reçu
                              </Button>
                            </>
                          )}
                        </div>
                        {s.recu_envoye_le && (
                          <p className="text-xs text-emerald-700 flex items-center gap-1" data-testid={`recu-envoye-le-${s.id}`}>
                            <CheckCircle className="h-3.5 w-3.5" /> Reçu envoyé le {new Date(s.recu_envoye_le).toLocaleString('fr-FR')}
                          </p>
                        )}
                        {s.recu_envoye_le && (
                          <Button variant="outline" size="sm" onClick={(e) => handleRelancerRecu(e, s)} className="h-8 text-xs gap-1.5 border-orange-200 text-orange-700 hover:bg-orange-50" data-testid={`relancer-recu-${s.id}`}>
                            <Bell className="h-3.5 w-3.5" /> Relancer
                          </Button>
                        )}
                        <div className="flex flex-wrap gap-1.5">
                          {!s.archive && (
                            <Button variant="outline" size="sm" onClick={(e) => handleOpenDialog(e, s, 'avenant')} className="h-8 text-xs gap-1.5">
                              <FilePlus className="h-3.5 w-3.5" /> Avenant
                            </Button>
                          )}
                          {/* V37.44 — Bouton Restaurer visible uniquement sur les archives */}
                          {s.archive && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => handleRestoreSouscription(e, s)}
                              className="h-8 text-xs gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-50"
                              data-testid={`restore-souscription-${s.id}`}
                            >
                              <ArchiveRestore className="h-3.5 w-3.5" /> Restaurer
                            </Button>
                          )}
                          {isSuperAdmin && !s.archive && (
                            <Button variant="outline" size="sm" onClick={(e) => handleDeleteSouscription(e, s)} className="h-8 text-xs gap-1.5 border-red-200 text-red-600 hover:bg-red-50" data-testid={`delete-souscription-${s.id}`}>
                              <Trash2 className="h-3.5 w-3.5" /> Archiver
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                    <RecuEnvoisHistorique souscriptionId={s.id} refreshKey={envoisRefresh} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showPreviewDialog && selectedSouscription && (
        <SouscriptionDocumentsPreviewDialog
          isOpen={showPreviewDialog}
          onClose={() => { setShowPreviewDialog(false); setSelectedSouscription(null); }}
          souscription={selectedSouscription}
          getAuthHeader={getAuthHeader}
        />
      )}

      {/* Dialog de modification */}
      {showModificationDialog && selectedSouscription && (
        <SouscriptionModificationDialog
          isOpen={showModificationDialog}
          onClose={() => { setShowModificationDialog(false); setSelectedSouscription(null); }}
          souscription={selectedSouscription}
          mode={dialogMode}
          onSuccess={() => { fetchSouscriptions(); setShowModificationDialog(false); setSelectedSouscription(null); }}
        />
      )}
    </div>
  );
}
